import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

// The focus ring itself is global (index.css :focus-visible).
const baseStyles =
  'inline-flex items-center justify-center gap-2 font-medium rounded-control transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

const sizeStyles: Record<Size, string> = {
  sm: 'px-3 py-2 text-label min-h-[32px]',
  md: 'px-4 py-2.5 text-body min-h-[40px]',
  lg: 'px-6 py-3 text-card min-h-[44px]',
};

const variantStyles: Record<Variant, string> = {
  primary: 'bg-brand text-white hover:bg-brand-hover active:bg-brand-active',
  secondary: 'bg-surface text-ink border border-line-strong hover:bg-neutral-tint active:bg-line',
  // Restrained on purpose: red text and border on white, never a solid red block.
  danger: 'bg-surface text-bad border border-bad/40 hover:bg-bad-tint hover:border-bad active:bg-bad-tint',
  ghost: 'bg-transparent text-neutral hover:bg-neutral-tint active:bg-line',
};

/** Button look for things that are not <button>s, e.g. a router <Link> that should look like one. */
export const buttonClass = (variant: Variant = 'primary', size: Size = 'md', className?: string) =>
  cn(baseStyles, sizeStyles[size], variantStyles[variant], className);

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: Variant;
  size?: Size;
  isLoading?: boolean;
}

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  isLoading = false,
  disabled,
  className,
  ...props
}: ButtonProps) => (
  <button
    disabled={disabled || isLoading}
    aria-busy={isLoading || undefined}
    className={buttonClass(variant, size, className)}
    {...props}
  >
    {children}
  </button>
);
